import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import { dispatchService, TeamNameCategory } from '../../../services/central-api/dispatch';
import { ClientError } from '../../../services/central-api/base-service';
import Toast from 'react-native-toast-message';
import { useStyles } from 'react-native-unistyles';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import CategoriesDropdown from './CategoriesDropdown';
import ActingBodiesCommandDropdown from '../../../components/Dropdowns/ActingBodiesCommandDropdown';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';
import { useAuth } from '../../../context/Auth';
import { useForm } from 'react-hook-form';
import MyAppText from '../../../components/MyAppText';
import CustomButton from '../../../components/CustomButton';
import { Tabs } from '../../../components/Tabs';
import { ScrollView } from 'react-native-web-hover';
import RtcMediaPlayer, { RtcMediaPlayerRef } from '../../../components/RtcMediaPlayer';
import { useQuery } from '@tanstack/react-query';
import { RouteProp, useFocusEffect, useIsFocused, useRoute } from '@react-navigation/native';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import { DispatchParamList } from '../../../typings/Params';
import { RootNavigation } from '../../../Routes';

interface FormTeamName {
    name: string;
    category: TeamNameCategory | null | '';
    actingBodyId: number | null;
    actingBodyUnitId: number | null;
    actingBodyCommandId: number | null;
    eventReceiverIdentifier: string;
}

export default function TeamName({ navigation }: {
    navigation: RootNavigation;
}) {
    const route = useRoute<RouteProp<DispatchParamList, 'TeamName'>>();
    const teamNameId = route.params?.id;

    const { theme } = useStyles();
    const { user: authenticatedUser } = useAuth();
    const rtcRef = useRef<RtcMediaPlayerRef>(null);
    const [currentTab, setCurrentTab] = useState<string>(route.params?.selectedTab ?? 'details');

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['teamName', teamNameId],
        queryFn: () => dispatchService.getTeamName(teamNameId!),
        enabled: !!teamNameId && !!authenticatedUser,
    });

    useEffect(() => {

        if (!authenticatedUser) {
            return;
        }

        if (!teamNameId) {
            form.reset({
                ...defaultEmptyValues,
                ...(!(authenticatedUser.isAdmin || authenticatedUser.permissions.dispatch_manage_all_acting_bodies_unit_names) && authenticatedUser.actingBodyUnit.actingBodyId ? {
                    actingBodyId: authenticatedUser.actingBodyUnit.actingBodyId
                } : {}),
                ...(!(authenticatedUser.isAdmin || authenticatedUser.permissions.dispatch_manage_all_acting_bodies_unit_names || authenticatedUser.permissions.dispatch_manage_acting_body_units_unit_names) && authenticatedUser.actingBodyUnit.actingBodyId ? {
                    actingBodyUnitId: authenticatedUser.actingBodyUnitId
                } : {})
            });
        }

        if (!data) {
            return;
        }

        form.reset({
            ...data,
            eventReceiverIdentifier: data.eventReceiverIdentifier || ''
        });

    }, [teamNameId, data, authenticatedUser]);


    const defaultEmptyValues: FormTeamName = {
        name: '',
        category: null,
        actingBodyId: null,
        actingBodyUnitId: null,
        actingBodyCommandId: null,
        eventReceiverIdentifier: '',
    };

    const form = useForm<FormTeamName>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused && teamNameId && authenticatedUser) {
            refetch();
        }
    }, [isFocused, teamNameId, authenticatedUser]));

    async function formSubmit() {
        try {
            const data = form.getValues();

            if (!data.actingBodyCommandId || !data.actingBodyUnitId) {
                return;
            }

            if (teamNameId) {
                await dispatchService.updateTeamName({
                    id: teamNameId,
                    actingBodyCommandId: data.actingBodyCommandId,
                    actingBodyUnitId: data.actingBodyUnitId,
                    category: data.category !== '' ? data.category : null,
                    name: data.name,
                    eventReceiverIdentifier: data.eventReceiverIdentifier != '' ? data.eventReceiverIdentifier : null,
                });
            } else if (data.actingBodyId) {
                await dispatchService.createTeamName({
                    actingBodyCommandId: data.actingBodyCommandId,
                    category: data.category !== '' ? data.category : null,
                    name: data.name,
                    actingBodyUnitId: data.actingBodyUnitId,
                    actingBodyId: data.actingBodyId,
                    eventReceiverIdentifier: data.eventReceiverIdentifier != '' ? data.eventReceiverIdentifier : null,
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Dispatch', { screen: 'TeamNames' });

        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function streamWhepUrl() {
        try {
            if (!rtcRef.current || currentTab != 'live' || !data?.whepUrl) {
                return;
            }

            await rtcRef.current.consume({ url: data.whepUrl });
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    useEffect(() => {
        streamWhepUrl();
    }, [currentTab, rtcRef, data]);

    return <Container>
        <Header
            goBackAction={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Dispatch', { screen: 'TeamNames' })}
            title={translate('registerTeamName')}
            actions={
                <CustomButton
                    text={translate('save')}
                    onPress={formSubmit}
                    disabled={!form.formState.isValid} />
            }
        />
        <Container>
            <Card>
                {(!!teamNameId && isLoading) ? <ActivityIndicator size='large' color={theme.colors.loadingColor} /> : <Tabs
                    tabs={[{
                        key: 'details',
                        label: translate('details'),
                    }, {
                        key: 'live',
                        label: translate('live'),
                        omit: teamNameId === undefined,
                    }]}
                    selectedTab={route.params ? route.params.selectedTab : undefined}
                >
                    {({ selectedTab }) => {
                        setCurrentTab(selectedTab);
                        if (selectedTab === 'details') {
                            return <ScrollView contentContainerStyle={{ rowGap: 10 }}>
                                {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_names) &&
                                    <ActingBodiesDropdown
                                        includeAllOption={false}
                                        zIndex={5}
                                        disabled={!!teamNameId}
                                        required={true}
                                        form={form}
                                        fieldName='actingBodyId'
                                    />
                                }
                                {(authenticatedUser?.isAdmin
                                    || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_names
                                    || authenticatedUser?.permissions.dispatch_manage_acting_body_units_unit_names
                                ) &&
                                    <ActingBodiesUnitsDropdown
                                        actingBodyId={form.watch('actingBodyId')}
                                        zIndex={4}
                                        required={true}
                                        form={form}
                                        fieldName='actingBodyUnitId'
                                    />
                                }
                                <ActingBodiesCommandDropdown
                                    actingBodyId={form.watch('actingBodyId')}
                                    disabled={false}
                                    zIndex={3}
                                    required={true}
                                    form={form}
                                    fieldName='actingBodyCommandId'
                                />
                                <FormInput
                                    label={translate('name')}
                                    required={true}
                                    form={form}
                                    fieldName='name'
                                />
                                <FormInput
                                    label={translate('eventReceiverIdentifier')}
                                    form={form}
                                    fieldName='eventReceiverIdentifier'
                                />
                                <CategoriesDropdown
                                    zIndex={2}
                                    form={form}
                                    fieldName='category'
                                    emptyOption={true}
                                    multiple={false}
                                />
                            </ScrollView>;
                        } else if (selectedTab === 'live' && teamNameId) {
                            return <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', gap: 10 }}>
                                {!data?.whepUrl ? <MyAppText>{translate('agentNotStreaming')}</MyAppText> : <RtcMediaPlayer ref={rtcRef} />}
                            </View>;
                        }
                    }}
                </Tabs>}
            </Card>
        </Container>
    </Container>;
}

